import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles, Info, Play, ShoppingBag, CircleDot } from 'lucide-react';
import { Logo } from './Logo';
import { SoundTherapyStep } from './ritual/SoundTherapyStep';
import { useClickSound } from '../hooks/useClickSound';
import { TOOL_COSTS } from '../hooks/useEnergyManager';
interface SoundCatalogProps {
  energyPoints: number;
  onDeductPoints: (amount: number) => void;
  onClose: () => void;
  onGoHome: () => void;
  onAddGlowDots?: (count: number) => void;
}
// Base cost from Dashboard (Balans Dźwiękiem)
const BASE_SOUND_COST = TOOL_COSTS.soundCatalog; // 40
// Duration constants
const DURATION_QUICK = 60; // Szybki Balans
const DURATION_DEEP = 60; // Głęboki Stan
const DURATION_PREMIUM = 120; // Binaural Beats
const FREQUENCIES = [{
  category: 'Szybki Balans (60s)',
  cost: BASE_SOUND_COST,
  duration: DURATION_QUICK,
  items: [{
    freq: 396,
    name: 'Uwolnienie Lęku',
    chakra: 'Podstawa',
    chakraIcon: '🔴',
    desc: 'Uwalnia głęboko zakorzeniony lęk',
    color: 'bg-red-50 text-red-700 border-red-200',
    rgb: '239, 68, 68'
  }, {
    freq: 417,
    name: 'Odcięcie Przeszłości',
    chakra: 'Sakralna',
    chakraIcon: '🟠',
    desc: 'Oczyszcza traumy i negatywne wzorce',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    rgb: '249, 115, 22'
  }, {
    freq: 432,
    name: 'Złoty Środek Natury',
    chakra: 'Serce',
    chakraIcon: '💚',
    desc: 'Naturalna częstotliwość harmonii',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rgb: '16, 185, 129'
  }, {
    freq: 528,
    name: 'Naprawa i Miłość',
    chakra: 'Splot Słoneczny',
    chakraIcon: '☀️',
    desc: 'Regeneracja DNA i transformacja',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    rgb: '250, 204, 21'
  }]
}, {
  category: 'Głęboki Stan (60s)',
  cost: BASE_SOUND_COST + 20,
  duration: DURATION_DEEP,
  items: [{
    freq: 639,
    name: 'Harmonia Relacji',
    chakra: 'Współodczuwanie',
    chakraIcon: '💗',
    desc: 'Wzmacnia empatię i harmonię',
    color: 'bg-pink-50 text-pink-700 border-pink-200',
    rgb: '236, 72, 153'
  }, {
    freq: 741,
    name: 'Przebudzenie Intuicji',
    chakra: 'Trzecie Oko',
    chakraIcon: '🔮',
    desc: 'Otwiera intuicję i mądrość',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    rgb: '99, 102, 241'
  }, {
    freq: 852,
    name: 'Powrót do Źródła',
    chakra: 'Korona',
    chakraIcon: '👑',
    desc: 'Łączy z wyższą świadomością',
    color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    rgb: '192, 38, 211'
  }]
}];
const PREMIUM_FREQUENCIES = [{
  frequencies: [432, 436],
  name: 'Fale Theta',
  desc: 'Dudnienia Różnicowe (4 Hz) — głęboka medytacja i regeneracja mózgu',
  gradient: 'bg-gradient-to-r from-emerald-50 to-teal-50 border-teal-200 text-teal-800',
  rgb: '20, 184, 166',
  secondaryRgb: '255, 255, 255',
  cost: BASE_SOUND_COST + 60,
  duration: 120
}, {
  frequencies: [528, 852],
  name: 'Złota Proporcja',
  desc: 'Kosmiczna Harmonia — naprawa komórkowa i duchowe przebudzenie',
  gradient: 'bg-gradient-to-r from-yellow-50 to-fuchsia-50 border-fuchsia-200 text-fuchsia-800',
  rgb: '250, 204, 21',
  secondaryRgb: '192, 38, 211',
  cost: BASE_SOUND_COST + 60,
  duration: 120
}];
export function SoundCatalog({
  energyPoints,
  onDeductPoints,
  onClose,
  onGoHome,
  onAddGlowDots
}: SoundCatalogProps) {
  const [activeSession, setActiveSession] = useState<{
    freq: number;
    frequencies?: number[];
    rgb: string;
    secondaryRgb?: string;
    duration: number;
    cost: number;
    isPremium?: boolean;
    soundName?: string;
    soundDesc?: string;
    soundIcon?: string;
  } | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const {
    playClick
  } = useClickSound();
  const handleStartSession = (freq: number, rgb: string, cost: number, duration: number, soundName?: string, soundDesc?: string, soundIcon?: string) => {
    playClick();
    if (energyPoints >= cost) {
      setActiveSession({
        freq,
        rgb,
        duration,
        cost,
        soundName,
        soundDesc,
        soundIcon
      });
    } else {
      alert('Brak wystarczającej liczby Punktów Energii.');
    }
  };
  const handleStartPremiumSession = (item: (typeof PREMIUM_FREQUENCIES)[0]) => {
    playClick();
    if (energyPoints >= item.cost) {
      setActiveSession({
        freq: item.frequencies[0],
        frequencies: item.frequencies,
        rgb: item.rgb,
        secondaryRgb: item.secondaryRgb,
        duration: item.duration,
        cost: item.cost,
        isPremium: true,
        soundName: item.name,
        soundDesc: item.desc,
        soundIcon: '✨'
      });
    } else {
      alert('Brak wystarczającej liczby Punktów Energii.');
    }
  };
  const handleSessionComplete = () => {
    if (activeSession) {
      onDeductPoints(activeSession.cost);
      // Add glow dots based on cost (1 glow dot per 20 energy points)
      const glowDots = Math.floor(activeSession.cost / 20);
      if (onAddGlowDots && glowDots > 0) {
        onAddGlowDots(glowDots);
      }
      setActiveSession(null);
    }
  };
  const handleCloseClick = () => {
    playClick();
    onClose();
  };
  const onOpenBoutique = () => {
    // Implement boutique open logic here if needed, or passed as prop
  };
  return <>
      <motion.div className="flex flex-col items-center w-full max-w-lg z-10 pb-8 mt-4 p-4 min-h-screen" initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} data-id="element-1025">
        {/* Header */}
        <div className="w-full flex items-center justify-between mb-6 px-2" data-id="element-1026">
          <button onClick={handleCloseClick} className="w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-500 hover:text-zen-accent transition-colors shadow-sm" data-id="element-1027">
            <ArrowLeft className="w-5 h-5" data-id="element-1028" />
          </button>
          <div className="flex flex-col items-center" data-id="element-1029">
            <Logo onClick={onGoHome} data-id="element-1030" />
            <span className="text-[10px] text-zen-accent font-medium tracking-widest uppercase mt-1" data-id="element-1031">
              Balans Dźwiękiem
            </span>
          </div>
          <div className="flex items-center space-x-1 bg-yellow-50/80 px-2 py-1 rounded-full border border-yellow-100" data-id="element-1032">
            <Sparkles className="w-3 h-3 text-yellow-500" fill="currentColor" data-id="element-1033" />
            <span className="text-xs font-bold text-yellow-600" data-id="element-1034">
              {energyPoints}
            </span>
          </div>
        </div>

        {/* Info Modal Trigger */}
        <button onClick={() => {
        playClick();
        setShowInfo(!showInfo);
      }} className="w-full clean-panel rounded-2xl p-4 mb-8 flex items-start space-x-3 hover:bg-slate-50 transition-colors text-left" data-id="element-1035">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5" data-id="element-1036">
            <Info className="w-4 h-4 text-blue-500" data-id="element-1037" />
          </div>
          <div data-id="element-1038">
            <h3 className="text-sm font-semibold text-zen-text mb-1" data-id="element-1039">
              Fizyka Rezonansu
            </h3>
            <p className="text-xs text-slate-500" data-id="element-1040">
              Dowiedz się, jak muzykoterapia komórkowa wpływa na Twój układ
              nerwowy. Kliknij, aby rozwinąć.
            </p>
            <AnimatePresence data-id="element-1041">
              {showInfo && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-1042">
                  <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-100 leading-relaxed" data-id="element-1043">
                    Dźwięk to fala mechaniczna, która przenika przez tkanki
                    ciała. Odpowiednio dobrane częstotliwości (np. Solfeggio)
                    potrafią synchronizować fale mózgowe, obniżać poziom
                    kortyzolu i stymulować nerw błędny, wprowadzając organizm w
                    stan głębokiej regeneracji.
                  </p>
                </motion.div>}
            </AnimatePresence>
          </div>
        </button>

        {/* Solfeggio Header */}
        <div className="w-full mb-6" data-id="element-1044">
          <h2 className="text-sm font-bold uppercase tracking-widest text-zen-accent text-center" data-id="element-1045">
            Częstotliwości Solfeggio
          </h2>
        </div>

        {/* Standard Categories */}
        <div className="w-full space-y-8" data-id="element-1046">
          {FREQUENCIES.map((category, idx) => <div key={idx} data-id="element-1047">
              <div className="flex items-center justify-between mb-4 px-1" data-id="element-1048">
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400" data-id="element-1049">
                  {category.category}
                </h3>
                <div className="text-[10px] font-bold text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full flex items-center border border-yellow-100" data-id="element-1050">
                  {category.cost}{' '}
                  <Sparkles className="w-2.5 h-2.5 ml-1" fill="currentColor" data-id="element-1051" />
                </div>
              </div>
              <div className="space-y-3" data-id="element-1052">
                {category.items.map(item => <button key={item.freq} onClick={() => handleStartSession(item.freq, item.rgb, category.cost, category.duration, item.name, item.desc, item.chakraIcon)} className={`w-full border rounded-2xl p-4 flex items-center space-x-4 transition-transform hover:scale-[1.02] active:scale-[0.98] text-left ${item.color}`} data-id="element-1053">
                    <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center text-xl shrink-0 shadow-sm" data-id="element-1054">
                      {item.chakraIcon}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1055">
                      <div className="flex items-center space-x-2 mb-0.5" data-id="element-1056">
                        <span className="text-sm font-bold" data-id="element-1057">
                          {item.freq} Hz
                        </span>
                        <span className="text-[9px] uppercase tracking-widest opacity-60 font-bold" data-id="element-1058">
                          {item.chakra}
                        </span>
                      </div>
                      <p className="text-xs font-semibold mb-0.5" data-id="element-1059">
                        {item.name}
                      </p>
                      <p className="text-[10px] opacity-60" data-id="element-1060">{item.desc}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white/50 flex items-center justify-center shrink-0" data-id="element-1061">
                      <Play className="w-4 h-4 ml-0.5" fill="currentColor" data-id="element-1062" />
                    </div>
                  </button>)}
              </div>
            </div>)}
        </div>

        {/* Premium Section */}
        <div className="w-full mt-10" data-id="element-1063">
          <div className="flex items-center space-x-2 mb-4 px-1" data-id="element-1064">
            <Sparkles className="w-4 h-4 text-yellow-500" fill="currentColor" data-id="element-1065" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-600" data-id="element-1066">
              Premium: Podwójny Rezonans
            </h3>
          </div>
          <div className="space-y-3" data-id="element-1067">
            {PREMIUM_FREQUENCIES.map((item, idx) => <button key={idx} onClick={() => handleStartPremiumSession(item)} className={`w-full border rounded-2xl p-5 flex flex-col items-center text-center transition-all hover:shadow-md active:scale-[0.98] ${item.gradient}`} data-id="element-1068">
                <div className="flex items-center space-x-1 mb-2" data-id="element-1069">
                  <Sparkles className="w-3 h-3 text-yellow-500" fill="currentColor" data-id="element-1070" />
                  <span className="text-[9px] uppercase tracking-widest font-bold text-yellow-600" data-id="element-1071">
                    Binaural Beats
                  </span>
                </div>
                <span className="text-xl font-bold mb-1" data-id="element-1072">
                  {item.frequencies[0]} Hz + {item.frequencies[1]} Hz
                </span>
                <span className="text-sm font-semibold mb-1" data-id="element-1073">{item.name}</span>
                <span className="text-[10px] opacity-70 leading-relaxed max-w-xs" data-id="element-1074">
                  {item.desc}
                </span>
                <div className="mt-3 flex items-center space-x-2" data-id="element-1075">
                  <div className="text-[10px] font-bold text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full flex items-center" data-id="element-1076">
                    {item.cost}{' '}
                    <Sparkles className="w-2.5 h-2.5 ml-1" fill="currentColor" data-id="element-1077" />
                  </div>
                  <span className="text-[10px] opacity-50" data-id="element-1078">•</span>
                  <span className="text-[10px] opacity-50" data-id="element-1079">
                    {item.duration}s
                  </span>
                </div>
              </button>)}
          </div>
        </div>

        {/* Upsell */}
        <div className="w-full mt-10 clean-panel rounded-3xl p-5 bg-gradient-to-br from-zen-bg to-yellow-50/30 relative overflow-hidden" data-id="element-1080">
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-100 rounded-full blur-3xl -mr-10 -mt-10 opacity-50" data-id="element-1081"></div>
          <div className="flex items-start space-x-4 relative z-10" data-id="element-1082">
            <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0 border border-slate-100" data-id="element-1083">
              <ShoppingBag className="w-6 h-6 text-zen-accent" data-id="element-1084" />
            </div>
            <div data-id="element-1085">
              <div className="inline-flex items-center space-x-1 bg-yellow-100 text-yellow-700 text-[10px] px-2 py-0.5 rounded-full font-bold mb-2" data-id="element-1086">
                +2500 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-1087" />
              </div>
              <h4 className="text-sm font-bold text-zen-text mb-1" data-id="element-1088">
                Złoty Kamerton 528 Hz
              </h4>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed" data-id="element-1089">
                Przenieś terapię do świata fizycznego. Kup fizyczny kamerton i
                odbierz gigantyczny bonus energetyczny w aplikacji.
              </p>
              <button onClick={() => {
              playClick();
              onOpenBoutique();
            }} className="w-full bg-zen-accent text-white rounded-xl py-3 text-sm font-bold hover:bg-zen-accent/90 transition-colors shadow-lg flex items-center justify-center space-x-2" data-id="element-1090">
                <ShoppingBag className="w-4 h-4" data-id="element-1091" />
                <span data-id="element-1092">Kup Teraz</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence data-id="element-1093">
        {activeSession && <SoundTherapyStep frequency={activeSession.freq} frequencies={activeSession.frequencies} colorRGB={activeSession.rgb} secondaryColorRGB={activeSession.secondaryRgb} duration={activeSession.duration} cost={activeSession.cost} isStandalone={true} isPremium={activeSession.isPremium} soundName={activeSession.soundName} soundDesc={activeSession.soundDesc} soundIcon={activeSession.soundIcon} onComplete={handleSessionComplete} onClose={() => setActiveSession(null)} data-id="element-1094" />}
      </AnimatePresence>
    </>;
}